import './TeamMembersViewDatatable.scss';

import { Cancel, Check, ContentCopy } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Chip, Divider, IconButton } from '@mui/material';
import { EventStatusEnum } from 'domain/event/EventStatusEnum';
import { PlayerRoleEnum } from 'domain/event/PlayerRoleEnum';
import { PlayerStatusEnum } from 'domain/event/PlayerStatusEnum';
import { PlayerTeamInvitationTypeEnum } from 'domain/event/PlayerTeamInvitationTypeEnum';
import { TagPlayer } from 'primary/Components/TagPlayer/TagPlayer';
import { FormEditRoleField } from 'primary/events/modals/ModalTeamEvent';
import { useContextDependency } from 'primary/hooks/useContextDependency';
import { useTranslate } from 'primary/hooks/useTranslate';
import { handleCopyLink } from 'primary/InvitationLink/invitationLink.func';
import { TeamsViewDatatableParametersProps } from 'primary/teams/ResponsiveTeamMembersView';
import { getPlayerStatusObject } from 'primary/teams/TeamMembersViewDatatable';
import React from 'react';

export const TeamMembersViewMobile = ({
  team,
  event,
  reFetch,
  disabled,
  onDecline,
}: TeamsViewDatatableParametersProps) => {
  const { teamRepository, authRepository } = useContextDependency();
  const t = useTranslate();
  const [isLoading, setIsLoading] = React.useState(false);

  const onClickLeaveTeam = () => () => {
    setIsLoading(true);
    teamRepository
      .leave(team.id)
      .then(() => {
        onDecline?.();
      })
      .finally(() => setIsLoading(false));
  };

  const onClickAccept = () => () => {
    setIsLoading(true);
    teamRepository
      .acceptTeamInvitationEvent(true, team.id, event?.id as number)
      .then(() => {
        reFetch();
      })
      .finally(() => setIsLoading(false));
  };

  const onClickDecline = () => () => {
    setIsLoading(true);
    teamRepository
      .declineTeamInvitationEvent(event?.id as number, team.id)
      .then(() => {
        onDecline();
      })
      .finally(() => setIsLoading(false));
  };

  const onClickExcludePlayer = (playerId: number) => () => {
    setIsLoading(true);
    teamRepository
      .excludePlayer(playerId)
      .then(() => reFetch())
      .finally(() => setIsLoading(false));
  };

  const onClickAcceptJoin = (playerId: number) => () => {
    setIsLoading(true);
    teamRepository
      .acceptTeamInvitationJoin(playerId)
      .then(() => {
        reFetch();
      })
      .finally(() => setIsLoading(false));
  };

  const onClickDeclineJoin = (playerId: number) => () => {
    setIsLoading(true);
    teamRepository
      .declineTeamInvitationJoin(playerId)
      .then(() => {
        reFetch();
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <div className={'teamsMemberViewMobile'}>
      {team.players
        .sort((p1, p2) => p2.id - p1.id)
        .map((player) => {
          const isCurrentPlayer =
            authRepository.currentUser?.userProfil?.id === player?.player?.idProfil;
          const missingPlayer = !player?.player && !player?.invitationLink;
          const isCurrentUserChief =
            team.players.find(
              (value) =>
                value?.player?.idProfil === authRepository.currentUser?.userProfil.id,
            )?.role === PlayerRoleEnum.CHIEF;

          return (
            <div className={'-teamMemberItemMobile'}>
              <div className={'-header'}>
                {player?.invitationLink && !player?.player && (
                  <span>Joueur en attente d'inscription</span>
                )}
                {player?.player && <TagPlayer player={player.player} />}
                {isCurrentUserChief && !isCurrentPlayer ? (
                  <FormEditRoleField
                    key={player?.player?.idProfil}
                    player={player}
                    initialRole={player?.role}
                    team={team}
                    onSubmitted={() => {
                      reFetch();
                    }}
                  />
                ) : (
                  <span>{t(`enum.playerRole.${player?.role as PlayerRoleEnum}`)}</span>
                )}
                <Chip
                  label={getPlayerStatusObject(player, t)?.label}
                  variant="outlined"
                  //@ts-ignore
                  color={
                    player?.player ? getPlayerStatusObject(player, t)?.state : 'warning'
                  }
                />
              </div>
              <Divider className="divider" />
              <div className={'-actions'}>
                {missingPlayer && <></>}
                {event?.status !== EventStatusEnum.PUBLISH && (
                  <span>événement démarré, aucune action possible</span>
                )}
                {event?.status === EventStatusEnum.PUBLISH &&
                  isCurrentUserChief &&
                  player?.invitationLink &&
                  !player?.player && (
                    <div className={'-copyLink'}>
                      <span>{t('event.teams.datatable.copyLink')}</span>
                      <IconButton onClick={handleCopyLink(player?.invitationLink)}>
                        <ContentCopy />
                      </IconButton>
                    </div>
                  )}
                {event?.status === EventStatusEnum.PUBLISH &&
                  isCurrentUserChief &&
                  player?.player &&
                  player?.typeInvitation === PlayerTeamInvitationTypeEnum.JOIN &&
                  player?.status === PlayerStatusEnum.PENDING && (
                    <>
                      <LoadingButton
                        variant={'contained'}
                        color={'primary'}
                        onClick={onClickAcceptJoin(player?.id)}
                        disabled={disabled}
                        loading={isLoading}
                      >
                        <Check />
                      </LoadingButton>
                      <LoadingButton
                        variant={'contained'}
                        color={'secondary'}
                        onClick={onClickDeclineJoin(player?.id)}
                        disabled={disabled}
                        loading={isLoading}
                      >
                        <Cancel />
                      </LoadingButton>
                    </>
                  )}
                {event?.status === EventStatusEnum.PUBLISH &&
                  isCurrentUserChief &&
                  !isCurrentPlayer &&
                  player?.player &&
                  player?.typeInvitation === PlayerTeamInvitationTypeEnum.INVITE &&
                  player?.status === PlayerStatusEnum.PENDING && (
                    <LoadingButton
                      variant={'contained'}
                      color={'secondary'}
                      onClick={onClickDeclineJoin(player?.id)}
                      disabled={disabled}
                      loading={isLoading}
                    >
                      <Cancel />
                    </LoadingButton>
                  )}
                {event?.status === EventStatusEnum.PUBLISH &&
                  !isCurrentUserChief &&
                  isCurrentPlayer &&
                  player?.player &&
                  player?.typeInvitation === PlayerTeamInvitationTypeEnum.INVITE &&
                  player?.status === PlayerStatusEnum.PENDING && (
                    <>
                      <LoadingButton
                        variant={'contained'}
                        color={'primary'}
                        onClick={onClickAccept()}
                        disabled={disabled}
                        loading={isLoading}
                      >
                        <Check />
                      </LoadingButton>
                      <LoadingButton
                        variant={'contained'}
                        color={'secondary'}
                        onClick={onClickDecline()}
                        disabled={disabled}
                        loading={isLoading}
                      >
                        <Cancel />
                      </LoadingButton>
                    </>
                  )}
                {event?.status === EventStatusEnum.PUBLISH &&
                  isCurrentPlayer &&
                  !isCurrentUserChief &&
                  player?.status === PlayerStatusEnum.ACCEPTED && (
                    <LoadingButton
                      variant={'contained'}
                      color={'secondary'}
                      onClick={onClickLeaveTeam()}
                      title={
                        !isCurrentUserChief
                          ? t('event.teams.datatable.leaveTeamNotice')
                          : undefined
                      }
                      disabled={disabled}
                      loading={isLoading}
                    >
                      {t('event.teams.datatable.leaveTeam')}
                    </LoadingButton>
                  )}
                {event?.status === EventStatusEnum.PUBLISH &&
                  !isCurrentPlayer &&
                  isCurrentUserChief &&
                  player?.status !== PlayerStatusEnum.PENDING && (
                    <>
                      <LoadingButton
                        variant={'contained'}
                        color={'secondary'}
                        onClick={onClickExcludePlayer(player?.id)}
                        disabled={disabled}
                        loading={isLoading}
                      >
                        {t('event.teams.datatable.deletePlayer')}
                      </LoadingButton>
                    </>
                  )}
              </div>
            </div>
          );
        })}
    </div>
  );
};
